// manual-subscription-billing — WU8: OrganizationSubscriptionSection.
//
// Super Admin drill-down of a single org's subscription invoices, itemized
// (base/overage) lines, and a "Record payment" action for OPEN manual
// invoices (design §3.3, spec Capability 4 — REQ-4.7: automatic invoices are
// reconciled exclusively via Stripe, so the action is scoped to
// `providerInvoiceRef === null`). Presentational — receives `orgId` as a
// prop (rather than reading route params itself) so it can be tested without
// a router context, mirroring the container/presentational split used
// elsewhere in this app.

import { ApiError, listOrganizationSubscriptionInvoices } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type {
  RecordManualSubscriptionPaymentResponse,
  SubscriptionInvoiceResponse,
} from '@jq4race/shared';
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  formatCents,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { enumLabel } from '../../../../shared/i18n/enum-label';
import { RecordManualPaymentDialog } from './RecordManualPaymentDialog';

const INVOICE_STATUS_TONE: Record<string, BadgeProps['variant']> = {
  open: 'warning',
  paid: 'success',
  uncollectible: 'danger',
  void: 'secondary',
};

interface OrganizationSubscriptionSectionProps {
  orgId: string;
}

export function OrganizationSubscriptionSection({ orgId }: OrganizationSubscriptionSectionProps) {
  const { t, i18n } = useTranslation('organizations');
  const [invoices, setInvoices] = useState<SubscriptionInvoiceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [payingInvoice, setPayingInvoice] = useState<SubscriptionInvoiceResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listOrganizationSubscriptionInvoices(orgId);
      setInvoices(res.invoices);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t, { fallback: t('subscriptionSection.loadErrorFallback') })
          : translateApiError(e, t, { fallback: t('subscriptionSection.loadErrorFallback') }),
      );
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function handleRecorded(result: RecordManualSubscriptionPaymentResponse) {
    setInvoices((prev) => prev.map((inv) => (inv.id === result.invoice.id ? result.invoice : inv)));
    setSuccess(t('subscriptionSection.paymentRecorded'));
  }

  return (
    <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
      <CardContent className="p-6 sm:p-8">
        <h2 className="mb-6 text-[15px] font-medium text-jq-charcoal">
          {t('subscriptionSection.heading')}
        </h2>

        {success && (
          <p
            role="status"
            className="mb-4 rounded-sm border border-jq-success/40 bg-jq-success/10 px-3 py-2 text-[13px] text-jq-charcoal"
          >
            {success}
          </p>
        )}

        {error && !loading ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <p className="text-[13px] text-jq-graphite">{t('subscriptionSection.loadError')}</p>
            <Button size="sm" variant="secondary" onClick={() => void reload()}>
              {t('subscriptionSection.retry')}
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('subscriptionSection.columns.period')}</TableHead>
                <TableHead>{t('subscriptionSection.columns.breakdown')}</TableHead>
                <TableHead>{t('subscriptionSection.columns.amount')}</TableHead>
                <TableHead>{t('subscriptionSection.columns.status')}</TableHead>
                <TableHead className="text-right">
                  {t('subscriptionSection.columns.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && invoices.length === 0 ? (
                <TableEmpty colSpan={5} message={t('subscriptionSection.empty')} />
              ) : (
                invoices.map((inv, idx) => {
                  // manual invoices carry providerInvoiceRef=null (multiple
                  // coexist — Postgres NULL-distinct uniqueness, design
                  // §1.2), so `invoiceNumber` (unique per org) is the
                  // preferred key; fall back to index only if both are null
                  // (shouldn't happen — every invoice has at least one).
                  const key = inv.id ?? inv.invoiceNumber ?? inv.providerInvoiceRef ?? idx;
                  // REQ-4.7 — manual payment recording is reachable only for
                  // manual invoices (providerInvoiceRef=null); automatic
                  // invoices reconcile exclusively via Stripe webhooks.
                  const canRecordPayment =
                    inv.status === 'open' && inv.providerInvoiceRef === null && inv.id;
                  return (
                    <TableRow key={key}>
                      <TableCell className="text-jq-graphite">
                        {inv.periodStart && inv.periodEnd
                          ? `${formatDate(inv.periodStart, i18n.language)} – ${formatDate(inv.periodEnd, i18n.language)}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-jq-graphite">
                        {inv.lines.length === 0 ? (
                          '—'
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {/* `kind` is a stable key here: an invoice has
                                at most one base and one overage line
                                (design §2.1). */}
                            {inv.lines.map((line) => (
                              <Badge
                                key={line.kind}
                                variant={line.kind === 'base' ? 'outline' : 'accent'}
                                title={line.description}
                              >
                                {enumLabel(t, 'invoiceLineKind', line.kind)}:{' '}
                                {formatCents(line.amountCents, inv.currency)}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-jq-graphite">
                        {formatCents(inv.amountCents, inv.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={INVOICE_STATUS_TONE[inv.status]} className="capitalize">
                          {enumLabel(t, 'invoiceStatus', inv.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canRecordPayment ? (
                          <Button size="sm" variant="outline" onClick={() => setPayingInvoice(inv)}>
                            {t('subscriptionSection.recordPayment')}
                          </Button>
                        ) : (
                          <span className="text-[12px] text-jq-graphite/60">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <RecordManualPaymentDialog
        orgId={orgId}
        invoice={payingInvoice}
        onOpenChange={(open) => {
          if (!open) setPayingInvoice(null);
        }}
        onRecorded={(result) => {
          handleRecorded(result);
          setPayingInvoice(null);
        }}
      />
    </Card>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
